"use client"

import { PricingPlan } from "@/app/types/PricingPlan"
import { Subscription } from "@/app/types/Subscription"
import { PricingSwitch } from "./pricing-table/pricing-switch"
import { PricingLoadingOverlay } from "./pricing-table/pricing-loading-overlay"
import { PricingSectionHeader } from "./pricing-table/pricing-section-header"
import { PricingTrialSection } from "./pricing-table/pricing-trial-section"
import { PricingPlansGrid } from "./pricing-table/pricing-plans-grid"
import { usePricingTable } from "./pricing-table/use-pricing-table"
import { PaygSection } from "./pricing-table/payg-section"
import type { PaygPricingItem, PricingMode } from "./pricing-table/types"
import type { PaygAutoRenewSettings } from "@/services/billing"

// Main Component
export interface PricingTableProps {
    pricing: { planType: 'starter' | 'pro' | 'advanced', credits: number, price: number, interval: string, originalPrice: number, hasDiscount?: boolean }[];
    changePlan?: (plan: PricingPlan) => void;
    downgrade?: () => void;
    currentSubscription?: Subscription;
    publicMode?: boolean;
    onTrialStart?: () => void;
    discountApplied?: boolean;
    onPlanUpdate?: (planType: 'starter' | 'pro' | 'advanced', isYearly: boolean) => void;
    onPaygCheckout?: (amountCents: number, autoRenew: boolean) => void;
    onModeChange?: (mode: PricingMode) => void;
    initialPlanType?: 'starter' | 'pro' | 'advanced';
    initialIsYearly?: boolean;
    initialMode?: PricingMode;
    initialPaygAmountCents?: number;
    initialAutoRenew?: boolean;
    paygPricing?: PaygPricingItem | null;
    paygPricingError?: boolean;
    isLoading?: boolean;
    isEnterprise?: boolean;
    isPricingLoading?: boolean;
    paygAutoRenewSettings?: PaygAutoRenewSettings | null;
    onPaygAutoRenewChange?: (enabled: boolean, topUpAmountCents: number) => Promise<boolean> | boolean;
    onPaygStateChange?: (amountCents: number, autoRenew: boolean) => void;
    showPayg?: boolean;
}

export default function PricingTable({
    pricing,
    changePlan,
    currentSubscription,
    publicMode,
    onTrialStart,
    discountApplied,
    onPlanUpdate,
    onPaygCheckout,
    onModeChange,
    initialPlanType,
    initialIsYearly,
    initialMode,
    initialPaygAmountCents,
    initialAutoRenew,
    paygPricing,
    paygPricingError,
    isLoading,
    isPricingLoading,
    paygAutoRenewSettings,
    onPaygAutoRenewChange,
    onPaygStateChange,
    showPayg = false,
}: PricingTableProps) {
    const effectiveInitialMode = !showPayg && initialMode === 'payg'
        ? 'annual'
        : initialMode;
    const {
        mode,
        isYearly,
        paygAmountCents,
        setPaygAmountCents,
        autoRenew,
        setAutoRenew,
        isLoadingInner,
        loadingPlan,
        toggleYearly,
        handlePlanSelect,
        handlePaygCheckout,
        defaultPlans,
    } = usePricingTable({
        currentSubscription,
        publicMode,
        changePlan,
        onPlanUpdate,
        onPaygCheckout,
        onModeChange,
        initialPlanType,
        initialIsYearly,
        initialMode: effectiveInitialMode,
        initialPaygAmountCents,
        initialAutoRenew,
        paygPricing,
    });

    const isPaygMode = showPayg && mode === 'payg';

    return (
        <div className="py-8 relative">
            <div className='mx-auto w-full'>
                <PricingLoadingOverlay show={!!(isLoading || isLoadingInner || isPricingLoading)} />

                <PricingSectionHeader publicMode={publicMode} />

                {!isPaygMode && (
                    <PricingTrialSection
                        publicMode={publicMode}
                        onTrialStart={onTrialStart}
                        onPlanSelectForTrial={publicMode ? () => handlePlanSelect(defaultPlans.find((p) => p.type === 'pro')!) : undefined}
                        currentSubscription={currentSubscription}
                    />
                )}

                <div className="flex w-full justify-center px-4">
                    <PricingSwitch
                        onSwitch={toggleYearly}
                        mode={mode}
                        showPayg={showPayg}
                    />
                </div>
                {isPaygMode && paygPricing ? (
                    <PaygSection
                        pricing={paygPricing}
                        amountCents={paygAmountCents}
                        onAmountChange={(amountCents) => {
                            setPaygAmountCents(amountCents)
                        }}
                        onAmountCommit={(amountCents) => {
                            // This amount is the next checkout's selected top-up.
                            // Do not PATCH Auto Renewal while a slider is being
                            // adjusted: doing so produces repeated requests and
                            // can fail before checkout has saved a payment method.
                            onPaygStateChange?.(amountCents, autoRenew)
                        }}
                        autoRenew={autoRenew}
                        autoRenewTopUpAmountCents={paygAutoRenewSettings?.topUpAmountCents}
                        onAutoRenewChange={async (enabled) => {
                            // Enabling without a saved card is checkout intent,
                            // not a settings mutation. Disabling is pessimistic.
                            if (enabled && paygAutoRenewSettings?.requiresPaymentMethod) {
                                setAutoRenew(true)
                                return
                            }
                            const saved = await onPaygAutoRenewChange?.(enabled, paygAmountCents)
                            if (saved !== false) {
                                setAutoRenew(enabled)
                                onPaygStateChange?.(paygAmountCents, enabled)
                            }
                        }}
                        onCheckout={handlePaygCheckout}
                        isLoading={!!isLoadingInner}
                        autoRenewDisabled={
                            !!isLoadingInner ||
                            (!!paygAutoRenewSettings && !paygAutoRenewSettings.canManage)
                        }
                        autoRenewMessage={
                            paygAutoRenewSettings && !paygAutoRenewSettings.canManage
                                ? "Only your team owner can manage Auto Renewal."
                                : paygAutoRenewSettings?.requiresPaymentMethod
                                    ? "A saved card is required. Select Auto Renewal and complete checkout to add one."
                                    : undefined
                        }
                    />
                ) : isPaygMode ? (
                    <section className="mx-auto mt-8 max-w-[720px] px-4" role="alert">
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-5 text-center text-sm text-red-800">
                            {paygPricingError
                                ? "Pay As You Go pricing is unavailable right now. Please refresh and try again later."
                                : "Loading Pay As You Go pricing…"}
                        </div>
                    </section>
                ) : (
                    <PricingPlansGrid
                        plans={defaultPlans}
                        currentSubscription={currentSubscription}
                        pricing={pricing}
                        isYearly={isYearly}
                        loadingPlan={loadingPlan}
                        publicMode={publicMode}
                        discountApplied={discountApplied}
                        onPlanSelect={handlePlanSelect}
                    />
                )}
            </div>
        </div>
    );
}
